import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Modal, Select, Switch } from "antd";

import CodeCitationPicker, { CodeCitationValue } from "components/code-citation-picker/code-citation-picker";
import { PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS, PLAN_REVIEW_SEVERITY_OPTIONS } from "constants/constants";

export interface PlanReviewRuleFormValues {
	id?: string;
	requirement_type: string;
	category_id: string;
	code: string;
	title: string;
	description: string;
	applicable_codes: string;
	codeCitations: CodeCitationValue[];
	source: string;
	section: string;
	source_url: string;
	default_severity: string;
	required: boolean;
	sort_order: number;
	submissionTypeIds: string[];
}

interface SubmissionTypeOption {
	id: string;
	name: string;
}

interface CategoryOption {
	value: string;
	label: string;
}

interface PlanReviewRuleModalProps {
	open: boolean;
	/** Whose adopted codes the citation picker searches. */
	jurisdictionId: string;
	// Absent when adding; the rule being edited otherwise.
	initialValues?: PlanReviewRuleFormValues | null;
	submissionTypes: SubmissionTypeOption[];
	categoryOptions: CategoryOption[];
	saving?: boolean;
	error?: string | null;
	onCancel: () => void;
	onSave: (values: PlanReviewRuleFormValues) => void;
}

const EMPTY: PlanReviewRuleFormValues = {
	requirement_type: PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS[0].value,
	category_id: "",
	code: "",
	title: "",
	description: "",
	applicable_codes: "",
	codeCitations: [],
	source: "",
	section: "",
	source_url: "",
	default_severity: PLAN_REVIEW_SEVERITY_OPTIONS[2].value,
	required: true,
	sort_order: 0,
	submissionTypeIds: []
};

/**
 * Create or edit one rule of the jurisdiction's rules container.
 *
 * At least one submission type is mandatory — the database enforces it with a
 * deferred constraint, so the form refuses to submit without one rather than letting
 * the mutation come back with a constraint violation.
 */
const PlanReviewRuleModal = ({
	open,
	jurisdictionId,
	initialValues,
	submissionTypes,
	categoryOptions,
	saving = false,
	error,
	onCancel,
	onSave
}: PlanReviewRuleModalProps) => {
	const [values, setValues] = useState<PlanReviewRuleFormValues>(initialValues ?? EMPTY);
	const [touched, setTouched] = useState(false);

	// Re-seed whenever the modal opens on a different rule. A new rule opens on the
	// first category, which is the one an office files most of its rules under.
	useEffect(() => {
		if (open) {
			setValues(initialValues ?? { ...EMPTY, category_id: categoryOptions[0]?.value ?? "" });
			setTouched(false);
		}
	}, [open, initialValues, categoryOptions]);

	const set = useCallback(
		<K extends keyof PlanReviewRuleFormValues>(key: K, value: PlanReviewRuleFormValues[K]) =>
			setValues(current => ({ ...current, [key]: value })),
		[]
	);

	const missing = useMemo(
		() => ({
			title: !values.title.trim(),
			description: !values.description.trim(),
			category: !values.category_id,
			submissionTypes: values.submissionTypeIds.length === 0
		}),
		[values]
	);
	const isValid = !missing.title && !missing.description && !missing.category && !missing.submissionTypes;

	const handleSave = useCallback(() => {
		setTouched(true);
		if (!isValid) return;
		onSave(values);
	}, [isValid, onSave, values]);

	return (
		<Modal open={open} onCancel={onCancel} footer={null} closable={false} width={760} className="prrm-modal">
			<h2 className="prrm-modal-title">{initialValues?.id ? "Edit rule" : "Add rule"}</h2>
			<p className="prrm-modal-sub">
				Submittals to this jurisdiction are screened against the description, so write it as what the package must
				contain or satisfy.
			</p>

			<div className="prrm-form">
				<div className="prrm-form-field prrm-form-field-full">
					<label className="prrm-label">Title</label>
					<Input
						className="prrm-control"
						placeholder="Rule name (e.g. Fire Lanes)"
						value={values.title}
						onChange={e => set("title", e.target.value)}
						status={touched && missing.title ? "error" : undefined}
					/>
				</div>

				<div className="prrm-form-field prrm-form-field-full">
					<label className="prrm-label">Description</label>
					<Input.TextArea
						className="prrm-control prrm-textarea"
						rows={4}
						placeholder="Describe what the submittal must contain or satisfy in order to be compliant. You can also define what constitutes non-compliance"
						value={values.description}
						onChange={e => set("description", e.target.value)}
						status={touched && missing.description ? "error" : undefined}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Rule type</label>
					<Select
						className="prrm-control"
						value={values.requirement_type}
						options={PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
						onChange={value => set("requirement_type", value)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Category</label>
					<Select
						className="prrm-control"
						value={values.category_id || undefined}
						placeholder="Which section of the report does it belong to?"
						options={categoryOptions}
						onChange={value => set("category_id", value)}
						status={touched && missing.category ? "error" : undefined}
					/>
				</div>

				<div className="prrm-form-field prrm-form-field-full">
					<label className="prrm-label">Applicable submittal types</label>
					<Select
						className="prrm-control"
						mode="multiple"
						placeholder="Which submittals is this rule evaluated for?"
						value={values.submissionTypeIds}
						options={submissionTypes.map(t => ({ label: t.name, value: t.id }))}
						onChange={value => set("submissionTypeIds", value)}
						status={touched && missing.submissionTypes ? "error" : undefined}
					/>
					<p className="prrm-hint">A rule has to apply to at least one submittal type.</p>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Severity</label>
					<Select
						className="prrm-control"
						value={values.default_severity}
						options={PLAN_REVIEW_SEVERITY_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
						onChange={value => set("default_severity", value)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">ID</label>
					<Input
						className="prrm-control"
						placeholder="TCESD2_FIRE_FLOW_TEST"
						value={values.code}
						onChange={e => set("code", e.target.value)}
					/>
					<p className="prrm-hint">
						A stable identifier for this rule. Left empty, the extraction can still match it by title.
					</p>
				</div>

				<div className="prrm-form-field prrm-form-field-full">
					<label className="prrm-label">Code citations</label>
					<CodeCitationPicker
						jurisdictionId={jurisdictionId}
						value={values.codeCitations}
						onChange={citations => set("codeCitations", citations)}
					/>
					<p className="prrm-hint">
						Choose any sections from adopted codes that apply to this rule, by section number or by words from its
						heading. Ollie will analyze them alongside the rule description when reviewing submittals. Each one is kept
						on the rule as a citation too — there is no need to write it again below.
					</p>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Reference document</label>
					<Input
						className="prrm-control"
						placeholder="Building Plan Submittal Guidelines, Oct. 2025"
						value={values.source}
						onChange={e => set("source", e.target.value)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Section</label>
					<Input
						className="prrm-control"
						placeholder="Doors and Egress"
						value={values.section}
						onChange={e => set("section", e.target.value)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Source URL</label>
					<Input
						className="prrm-control"
						placeholder="https://…"
						value={values.source_url}
						onChange={e => set("source_url", e.target.value)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Other codes</label>
					<Input
						className="prrm-control"
						placeholder="NFPA 72, NFPA 25"
						value={values.applicable_codes}
						onChange={e => set("applicable_codes", e.target.value)}
					/>
					<p className="prrm-hint">
						Comma separated, and only for what the picker above cannot cite: a code the platform does not hold (NFPA
						72), or a chapter with no section number. They are named in the report, but their text cannot be analyzed.
						The sections chosen above are not listed here and clearing this field does not remove them.
					</p>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Sort order</label>
					<Input
						className="prrm-control"
						type="number"
						value={values.sort_order}
						onChange={e => set("sort_order", Number(e.target.value) || 0)}
					/>
				</div>

				<div className="prrm-form-field">
					<label className="prrm-label">Required</label>
					<div>
						<Switch checked={values.required} onChange={checked => set("required", checked)} className="prrm-switch" />
						<span className="prrm-hint"> If not selected, this rule will be handled as optional / recommended.</span>
					</div>
				</div>
			</div>

			{error ? <p className="prrm-error">{error}</p> : null}

			<div className="prrm-modal-actions">
				<button type="button" className="prrm-btn" onClick={onCancel} disabled={saving}>
					Cancel
				</button>
				<button type="button" className="prrm-btn prrm-btn-primary" onClick={handleSave} disabled={saving}>
					{saving ? "Saving…" : "Save rule"}
				</button>
			</div>
		</Modal>
	);
};

export default PlanReviewRuleModal;
